import { useEffect, useState } from "react";
import { MaIcon } from "./ui/MaIcon";
import { useToggleFollow, useUserSearch } from "../lib/queries";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AddFriendsSheet({ open, onClose }: Props) {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const { data: results = [], isPending } = useUserSearch(debouncedQ);
  const toggleFollow = useToggleFollow();

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    if (open) setQ("");
  }, [open]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "absolute", inset: 0, zIndex: 40,
        background: "rgba(20,12,4,0.45)",
        display: "flex", flexDirection: "column", justifyContent: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--ma-paper)",
          borderTop: "var(--ma-border) solid var(--ma-ink)",
          borderTopLeftRadius: 22, borderTopRightRadius: 22,
          padding: "10px 0 calc(env(safe-area-inset-bottom, 0px) + 24px)",
          maxHeight: "80%", display: "flex", flexDirection: "column",
          boxShadow: "0 -10px 30px rgba(20,12,4,0.18)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", padding: "4px 0 10px" }}>
          <div style={{ width: 44, height: 4, borderRadius: 2, background: "var(--ma-ink)", opacity: 0.35 }} />
        </div>

        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "0 18px 12px" }}>
          <div className="ma-display" style={{ fontSize: 26 }}>Find friends</div>
          <button onClick={onClose} style={{
            background: "transparent", border: "none", padding: 0,
            fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
            textTransform: "uppercase", color: "var(--ma-ink-soft)", cursor: "pointer",
            textDecoration: "underline",
          }}>Done</button>
        </div>

        <div style={{ padding: "0 16px 12px" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "12px 14px",
            background: "var(--ma-cream)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
          }}>
            <MaIcon name="search" size={18} stroke={1.8} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by handle or name"
              autoFocus
              style={{
                flex: 1, border: "none", outline: "none",
                background: "transparent", fontFamily: "var(--ma-body)", fontSize: 15,
                color: "var(--ma-ink)", minWidth: 0,
              }}
            />
            {q && (
              <button onClick={() => setQ("")} aria-label="Clear" style={{
                background: "transparent", border: "none", cursor: "pointer", padding: 0,
                color: "var(--ma-ink-soft)",
              }}>
                <MaIcon name="close" size={14} stroke={2} />
              </button>
            )}
          </div>
        </div>

        <div style={{ overflowY: "auto", padding: "0 16px 8px", flex: 1 }}>
          {debouncedQ.length < 2 && (
            <div className="ma-mono" style={{
              fontSize: 11, color: "var(--ma-ink-soft)", padding: "16px 4px",
              letterSpacing: "0.08em",
            }}>
              Type at least 2 characters.
            </div>
          )}

          {debouncedQ.length >= 2 && isPending && (
            <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", padding: "16px 4px" }}>
              searching…
            </div>
          )}

          {debouncedQ.length >= 2 && !isPending && results.length === 0 && (
            <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", padding: "16px 4px" }}>
              No one with that name or handle.
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {results.map((u) => (
              <div key={u.id} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: 10,
                background: "var(--ma-cream)",
                border: "var(--ma-border) solid var(--ma-ink)",
                borderRadius: "var(--ma-card-radius)",
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: "50%",
                  background: `var(--ma-${u.avatarTone})`,
                  color: "var(--ma-paper)",
                  border: "var(--ma-border) solid var(--ma-ink)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "var(--ma-display)", fontSize: 18, fontWeight: 700,
                  flexShrink: 0,
                }}>{u.avatarInitial}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "var(--ma-ink)",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {u.name}
                  </div>
                  <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.08em",
                    textTransform: "uppercase", color: "var(--ma-ink-soft)", marginTop: 2 }}>
                    @{u.handle}
                  </div>
                </div>
                <button
                  onClick={() => toggleFollow.mutate({ userId: u.id, following: u.following })}
                  disabled={toggleFollow.isPending}
                  style={{
                    padding: "8px 14px",
                    background: u.following ? "var(--ma-cream)" : "var(--ma-ink)",
                    color: u.following ? "var(--ma-ink)" : "var(--ma-paper)",
                    border: "var(--ma-border) solid var(--ma-ink)",
                    borderRadius: "var(--ma-card-radius)",
                    fontFamily: "var(--ma-mono)", fontSize: 10, letterSpacing: "0.12em",
                    textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
                  }}>
                  {u.following ? "Following" : "Follow"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
